import React, { useState, useEffect, FunctionComponent, useRef, ChangeEvent, Fragment } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box, ClickAwayListener, Typography, useTheme, useMediaQuery } from '@material-ui/core';
import Pet from '@spike/pet-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MAX_IMAGE_UPLOAD_BYTES } from 'utils/ImageResizeUtils';
import { isStandardProfileImageFormatAllowed, normalizeImageToJpegIfFormatNotAllowed } from 'utils/imageFormatToJpeg';
import { getGalleryFileAccept, isIOSDevice } from 'utils/mobileImageGalleryAcept';
import { faCamera } from '@fortawesome/pro-light-svg-icons';
import clsx from 'clsx';
import ImageMenu from './ImageMenu';
import { isEmpty } from 'lodash';
import { CropperImage, MobilePhotoSourceSheet } from 'components/UI';
import { wbp, reduceResolution } from 'Theme';
import { PetTypeIds } from '@spike/model';

interface PetImageProps {
    pet: Pet | undefined;
    onChangePet: (pet: Pet) => void;
    className?: string;
    disableEdition?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        imageContainer: {
            position: 'relative',
            [theme.breakpoints.down(wbp)]: {
                width: `${reduceResolution(100)}px`,
                height: `${reduceResolution(100)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                width: '100px',
                height: '100px'
            },
            [theme.breakpoints.down('sm')]: {
                width: '75px',
                height: '75px'
            }
        },

        imageBox: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundImage: (props: PetImageProps) => 'url(' + props.pet?.image + ')',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            borderRadius: '50%',
            textAlign: 'center',
            position: 'relative',
            width: '100%',
            height: '100%'
        },

        image: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundImage: (props: PetImageProps) => 'url(' + props.pet?.image + ')',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            borderRadius: '50%',
            textAlign: 'center',
            position: 'relative',
            [theme.breakpoints.down(wbp)]: {
                width: `${reduceResolution(174)}px`,
                height: `${reduceResolution(174)}px`,
                marginRight: `${reduceResolution(44)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                width: '174px',
                height: '174px',
                marginRight: '44px'
            }
        },
        edit: {
            cursor: 'pointer',
            fontWeight: 600,
            textDecoration: 'underline',
            color: '#fff',
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(18)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '18px'
            }
        },
        emptyImage: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#F8F5F1',
            borderRadius: '50%',
            [theme.breakpoints.down(wbp)]: {
                width: `${reduceResolution(174)}px`,
                height: `${reduceResolution(174)}px`,
                marginRight: `${reduceResolution(44)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                width: '174px',
                height: '174px',
                marginRight: '44px'
            }
        },

        imageText: {
            fontWeight: 600,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(18)}px`,
                marginBottom: `${reduceResolution(9)}px`,
                paddingRight: `${reduceResolution(12)}px`,
                paddingLeft: `${reduceResolution(12)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '18px',
                marginBottom: '9px',
                paddingRight: '12px',
                paddingLeft: '12px'
            }
        },
        bgCat: {
            backgroundColor: '#FAEFDF'
        },
        bgDog: {
            backgroundColor: '#E9F0ED'
        },

        uploadIcon: {
            color: '#BAA997',
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(34)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '34px'
            },
            [theme.breakpoints.down('md')]: {
                color: '#92B4A7'
            }
        },

        uploadDog: {
            color: '#5E8677'
        },
        uploadCat: {
            color: '#EAB464 !important'
        },

        iconFace: {
            marginRight: '5px',
            [theme.breakpoints.up(wbp)]: {
                marginTop: '5px'
            }
        },
        itemTextEmpty: {
            color: '#D3D3D3'
        },

        warningBox: {
            backgroundColor: '#E9F0ED',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            borderRadius: '50%',
            textAlign: 'center'
        },

        imageBoxWithOpacity: {
            filter: 'brightness(0.8)'
        },

        sizeText: {
            fontWeight: 600,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(13)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '13px'
            }
        },

        editOptions: {
            left: '50%'
        },

        uploadTitle: {
            fontWeight: 500,
            textDecoration: 'underline',
            color: '#5E8677',
            cursor: 'pointer',
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(16)}px`,
                lineHeight: `${reduceResolution(30)}px`,
                marginBottom: `${reduceResolution(8)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '16px',
                lineHeight: '30px',
                marginBottom: '8px'
            }
        },

        uploadBox: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: '#E9F0ED',
            borderRadius: '50%',
            [theme.breakpoints.down('md')]: {
                'backgroundColor': '#BDD2CA',
                '& $uploadIcon': {
                    color: '#FFF'
                }
            }
        },

        uploadCatBox: {
            backgroundColor: '#FAEFDF !important',
            [theme.breakpoints.down('md')]: {
                'backgroundColor': '#F2D2A2 !important',
                '& $uploadIcon': {
                    color: '#C18733'
                }
            }
        }
    })
);

const DESKTOP_ACCEPT = '.png,.jpg,.jpeg';

export const PetImage: FunctionComponent<PetImageProps> = props => {
    const classes = useStyles(props);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTouchMobile =
        typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
    const useActionsSheet = isMobile || isTouchMobile;

    const [hover, setHover] = useState(false);
    const [pet, setPet] = useState<Pet | undefined>(props.pet);
    const [showEmptyImage, setEmptyImage] = useState(true);
    const [warningKind, setWarningKind] = useState<'none' | 'size' | 'format'>('none');
    const [showMenu, setShowMenu] = useState(false);
    const [showActionsSheet, setShowActionsSheet] = useState(false);

    const [selectedImage, setSelectedImage] = useState<string>();
    const [fileName, setFileName] = useState<string>();
    const [showCropper, setShowCropper] = useState(false);

    const inputFileRef = useRef<HTMLInputElement>(null);
    const galleryInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (props.pet) {
            setPet(props.pet);
            if (props.pet.image === undefined || props.pet.image === null || isEmpty(props.pet.image)) {
                setEmptyImage(true);
            } else {
                setEmptyImage(false);
            }
        }
    }, [props.pet]);

    const imageSelectionHandler = async (event: ChangeEvent<HTMLInputElement>) => {
        const inputEl = event.target;
        const rawFile = inputEl.files?.[0];
        inputEl.value = '';

        if (!rawFile) {
            return;
        }

        if (rawFile.size > MAX_IMAGE_UPLOAD_BYTES) {
            setWarningKind('size');
            return;
        }

        let file: File;
        try {
            file = await normalizeImageToJpegIfFormatNotAllowed(rawFile, isStandardProfileImageFormatAllowed);
        } catch {
            setWarningKind('format');
            return;
        }

        if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
            setWarningKind('size');
            return;
        }

        setWarningKind('none');
        const reader = new FileReader();

        reader.onload = () => {
            if (reader.readyState === 2) {
                setSelectedImage(reader.result?.toString()!);
                setFileName(file.name);
                setShowCropper(true);
            }
        };
        reader.readAsDataURL(file);
    };

    const openGalleryPicker = () => {
        galleryInputRef.current?.click();
        setShowActionsSheet(false);
    };

    const openCameraPicker = () => {
        cameraInputRef.current?.click();
        setShowActionsSheet(false);
    };

    const changeImageHandler = () => {
        if (isIOSDevice()) {
            openGalleryPicker();
            setShowMenu(false);
            return;
        }
        if (useActionsSheet) {
            setShowActionsSheet(true);
        } else {
            inputFileRef.current?.click();
        }
        setShowMenu(false);
    };

    const handleEmptyOrMobileOpen = () => {
        if (props.disableEdition) return;
        if (isIOSDevice()) {
            openGalleryPicker();
            return;
        }
        if (useActionsSheet) {
            setShowActionsSheet(true);
        } else {
            inputFileRef.current?.click();
        }
    };

    const handleImageAreaClickMobile = () => {
        if (!useActionsSheet || props.disableEdition) return;
        if (isIOSDevice()) {
            openGalleryPicker();
            return;
        }
        setShowActionsSheet(true);
    };

    const removeImageHandler = () => {
        if (pet) {
            const petAux: Pet = pet;
            petAux.image = '';
            petAux.imageFileToUpload = undefined;
            petAux.imageRemoved = true;
            props.onChangePet && props.onChangePet(petAux);
            setPet(petAux);
            setShowMenu(false);
            setEmptyImage(true);
        }
    };

    const cropHandler = async (file: File) => {
        const petAux: Pet = pet!;
        petAux.image = URL.createObjectURL(file);
        petAux.imageFileToUpload = file;
        petAux.imageRemoved = false;

        props.onChangePet && props.onChangePet(petAux);
        setPet(petAux);
        setWarningKind('none');
        setEmptyImage(false);
        setShowCropper(false);
    };

    const showWarning = warningKind !== 'none';

    const warningMessage =
        warningKind === 'format' ? (
            <Box>
                <Typography className={classes.imageText}>Could not load this image.</Typography>
                <Typography className={classes.sizeText}>Try JPG or PNG.</Typography>
            </Box>
        ) : (
            <Box>
                <Typography className={classes.imageText}>Image is too large.</Typography>
                <Typography className={classes.sizeText}>{`Max size: ${
                    MAX_IMAGE_UPLOAD_BYTES / (1024 * 1024)
                }MB`}</Typography>
            </Box>
        );

    const emptyImage = (
        <Box
            className={clsx(classes.uploadBox, {
                [classes.uploadCatBox]: pet && !isEmpty(pet.type) && pet.type?.id === PetTypeIds.CATS,
                [classes.warningBox]: showWarning
            })}
            onClick={handleEmptyOrMobileOpen}
        >
            {hover && (
                <Typography
                    className={clsx(
                        classes.uploadTitle,
                        { [classes.uploadCat]: pet && !isEmpty(pet.type) && pet.type?.id === PetTypeIds.CATS },
                        { [classes.uploadDog]: pet && !isEmpty(pet.type) && pet.type?.id === PetTypeIds.DOGS }
                    )}
                >
                    Upload
                </Typography>
            )}
            {!hover && !showWarning && (
                <FontAwesomeIcon
                    icon={faCamera}
                    className={clsx(
                        classes.uploadIcon,
                        { [classes.uploadCat]: pet && !isEmpty(pet.type) && pet.type?.id === PetTypeIds.CATS },
                        { [classes.uploadDog]: pet && !isEmpty(pet.type) && pet.type?.id === PetTypeIds.DOGS }
                    )}
                />
            )}
            {!hover && showWarning && warningMessage}
        </Box>
    );

    const image = (
        <Box
            className={clsx({
                [classes.warningBox]: showWarning,
                [classes.imageBox]: !showWarning,
                [classes.imageBoxWithOpacity]: !props.disableEdition && hover,
                [classes.uploadCatBox]: pet && !isEmpty(pet.type) && pet.type?.id === PetTypeIds.CATS
            })}
            onClick={handleImageAreaClickMobile}
            style={useActionsSheet && !props.disableEdition ? { cursor: 'pointer' } : undefined}
        >
            {showWarning && warningMessage}
            {hover && !props.disableEdition && !useActionsSheet && (
                <Typography
                    className={classes.edit}
                    onClick={e => {
                        e.stopPropagation();
                        setShowMenu(true);
                    }}
                >
                    Edit
                </Typography>
            )}
        </Box>
    );

    return (
        <Fragment>
            <Box
                className={props.className}
                style={{ position: 'relative' }}
            >
                {useActionsSheet ? (
                    <>
                        <input
                            style={{
                                position: 'absolute',
                                width: 0,
                                height: 0,
                                opacity: 0,
                                overflow: 'hidden'
                            }}
                            type="file"
                            ref={galleryInputRef}
                            onChange={imageSelectionHandler}
                            accept={getGalleryFileAccept()}
                            aria-hidden
                            tabIndex={-1}
                        />
                        <input
                            style={{
                                position: 'absolute',
                                width: 0,
                                height: 0,
                                opacity: 0,
                                overflow: 'hidden'
                            }}
                            type="file"
                            ref={cameraInputRef}
                            onChange={imageSelectionHandler}
                            accept="image/*"
                            capture="environment"
                            aria-hidden
                            tabIndex={-1}
                        />
                    </>
                ) : (
                    <input
                        style={{ display: 'none' }}
                        type="file"
                        ref={inputFileRef}
                        onChange={imageSelectionHandler}
                        accept={DESKTOP_ACCEPT}
                        value=""
                    />
                )}
                <ClickAwayListener onClickAway={() => setShowMenu(false)}>
                    <Box
                        className={clsx(classes.imageContainer, 'imageWrapper')}
                        onMouseLeave={() => setHover(false)}
                        onMouseOver={() => setHover(true)}
                    >
                        {showEmptyImage && emptyImage}
                        {!showEmptyImage && image}

                        {showMenu && !props.disableEdition && !isMobile && (
                            <ImageMenu
                                onChange={changeImageHandler}
                                onRemove={removeImageHandler}
                                onClose={() => setShowMenu(false)}
                                className={classes.editOptions}
                            />
                        )}
                    </Box>
                </ClickAwayListener>
            </Box>
            {useActionsSheet && (
                <MobilePhotoSourceSheet
                    open={showActionsSheet}
                    onClose={() => setShowActionsSheet(false)}
                    onPhotoGallery={openGalleryPicker}
                    onCamera={openCameraPicker}
                />
            )}
            {showCropper && (
                <CropperImage
                    image={selectedImage!}
                    fileName={fileName!}
                    cropWidth={256}
                    cropHeight={256}
                    onClose={() => setShowCropper(false)}
                    onCrop={cropHandler}
                />
            )}
        </Fragment>
    );
};

export default PetImage;
